/**
 * DIVIDER MOTION (Track B — Ola 1.1)
 * Movimiento real para separadores SVG, más allá del "breath" CSS:
 *   - parallax: olas multicapa que se desplazan a distinta velocidad AL SCROLL.
 *   - morph:    una ola que fluye/deforma su path de forma continua.
 *
 * RENDIMIENTO (clave): animar el atributo `d` de un path obliga a re-teselar +
 * re-rasterizar una forma full-width cada frame → caro y causa jank al scroll.
 *   - El PARALLAX usa `transform: translateX` sobre el path estático (que ya se
 *     extiende a x∈[-PAD, VIEW_W+PAD]): composición GPU, sin re-tesela/re-raster.
 *     `will-change: transform` se activa SOLO mientras el divider está en vista
 *     (evita capas de composición permanentes) y se limpia en teardown.
 *   - El MORPH sí cambia de forma (re-raster inevitable): 24fps, muestreo grueso,
 *     solo en viewport, EN PAUSA durante el scroll y con auto-stop del rAF.
 *
 * Fronteras: la inyección de dividers en render.js es compartida (solo append al
 * DIVIDER_CATALOG). No marcamos el SVG con clases en la inyección: identificamos
 * el tipo cotejando el `d` del primer path contra entradas con metadata `motion`.
 *
 * Se registra previewOnly → preview del editor + sitio público, nunca en edición.
 * Además registramos un teardown editorOnly para limpiar al pasar preview→editor.
 * Respeta prefers-reduced-motion (lectura en vivo) y degrada en táctil.
 */
import { IS_TOUCH } from './motion-tokens.js';
import { registerPostRender, DIVIDER_CATALOG } from '../core/render.js';
import { store } from '../core/store.js';

const PAD = 240;                    // overhang (user-units) → el shift nunca muestra bordes
const VIEW_W = 1440;
const VIEW_H = 80;
const STEP = IS_TOUCH ? 96 : 64;    // muestreo del morph (grueso = raster barato)
const MORPH_FRAME = 1000 / 24;      // cap del morph a 24fps
const SCROLL_IDLE = 160;            // ms tras el último scroll para reanudar el morph

let motionMap = null;
let parallaxItems = [];
let morphItems = [];
let io = null;
let scrollRaf = 0;
let morphRaf = 0;
let bound = false;
let lastScrollTs = -1e9;

/** Mapa lazy: `d` del primer path → entrada de catálogo con motion. */
function getMotionMap() {
    if (motionMap) return motionMap;
    motionMap = {};
    Object.values(DIVIDER_CATALOG).forEach(entry => {
        if (!entry || !entry.motion) return;
        const firstD = entry.paths?.[0]?.d ?? entry.path;
        if (firstD) motionMap[firstD] = entry;
    });
    return motionMap;
}

/** Genera el path de una ola muestreada (de -PAD a VIEW_W+PAD), rellenando hacia abajo. */
function buildWavePath(baseline, amp, freq, phase) {
    const x0 = -PAD, x1 = VIEW_W + PAD;
    const yAt = (x) => (baseline + amp * Math.sin(phase + (x / VIEW_W) * freq * Math.PI * 2)).toFixed(1);
    let d = 'M' + x0 + ',' + yAt(x0);
    for (let x = x0 + STEP; x < x1; x += STEP) d += ' L' + x.toFixed(1) + ',' + yAt(x);
    d += ' L' + x1 + ',' + yAt(x1);
    d += ' L' + x1 + ',' + VIEW_H + ' L' + x0 + ',' + VIEW_H + ' Z';
    return d;
}

function clearWillChange() {
    for (const item of parallaxItems) {
        for (const L of item.layers) {
            try { L.path.style.willChange = ''; } catch (e) { /* nodo detached */ }
        }
    }
}

function teardown() {
    if (io) { io.disconnect(); io = null; }
    if (scrollRaf) { cancelAnimationFrame(scrollRaf); scrollRaf = 0; }
    if (morphRaf) { cancelAnimationFrame(morphRaf); morphRaf = 0; }
    if (bound) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', scheduleParallax);
        bound = false;
    }
    clearWillChange();
    parallaxItems = [];
    morphItems = [];
}

function bindScroll() {
    if (bound) return;
    bound = true;
    // passive: no bloquea el scroll. Detecta scroll para mover el parallax y
    // pausar el morph mientras dura.
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', scheduleParallax, { passive: true });
}

function onScroll() {
    lastScrollTs = performance.now();
    if (parallaxItems.length) scheduleParallax();
}

function scheduleParallax() {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        updateParallax();
    });
}

/**
 * Parallax barato: solo `transform: translateX` sobre paths estáticos (composición
 * GPU). El read de getBoundingClientRect (sobre el <svg> padre) no provoca thrashing
 * porque los writes de transform van a los <path> hijos y no ensucian el bbox leído.
 */
function updateParallax() {
    const vh = window.innerHeight || 800;
    for (const item of parallaxItems) {
        if (!item.inView || !item.svg.isConnected) continue;
        const rect = item.svg.getBoundingClientRect();
        let p = (vh - rect.top) / (vh + rect.height);   // ~0..1 al cruzar el viewport
        p = p < 0 ? 0 : p > 1 ? 1 : p;
        for (const L of item.layers) {
            const shift = (p - 0.5) * L.range;           // centrado en p=0.5 → profundidad por capa
            L.path.style.transform = 'translateX(' + shift.toFixed(1) + 'px)';
        }
    }
}

function startMorph() {
    if (morphRaf) return;
    let last = 0;
    const tick = (t) => {
        morphRaf = requestAnimationFrame(tick);
        // Auto-stop: si ningún morph está en vista, parar el bucle (incluso durante scroll).
        let anyView = false;
        for (const item of morphItems) {
            if (item.inView && item.svg.isConnected) { anyView = true; break; }
        }
        if (!anyView) { cancelAnimationFrame(morphRaf); morphRaf = 0; return; }
        if (t - last < MORPH_FRAME) return;                 // cap 24fps
        if (t - lastScrollTs < SCROLL_IDLE) return;         // pausa mientras se hace scroll
        last = t;
        for (const item of morphItems) {
            if (!item.inView || !item.svg.isConnected) continue;
            for (const L of item.layers) {
                const phase = L.basePhase + t * L.speedT;
                const ampEff = L.amp * (1 + 0.12 * Math.sin(t * 0.0004 + L.basePhase));
                L.path.setAttribute('d', buildWavePath(L.baseline, ampEff, L.freq, phase));
            }
        }
    };
    morphRaf = requestAnimationFrame(tick);
}

function onIntersect(entries) {
    entries.forEach(e => {
        const item = e.target._dmItem;
        if (!item) return;
        item.inView = e.isIntersecting;
        // will-change solo mientras está en viewport → sin capas de composición permanentes.
        if (item.kind === 'parallax') {
            const wc = e.isIntersecting ? 'transform' : '';
            for (const L of item.layers) L.path.style.willChange = wc;
        }
    });
    if (parallaxItems.length) scheduleParallax();
    if (morphItems.some(m => m.inView)) startMorph();
}

export function initDividerMotion() {
    teardown();
    // reduced-motion en vivo (no en import-time): si cambia en sesión, lo respetamos al re-render.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (store.get('editorMode')) return;      // cinturón + tirantes (el hook ya es previewOnly)

    const root = document.getElementById('resultado') || document;
    const svgs = root.querySelectorAll('svg.section-divider');
    if (!svgs.length) return;

    const map = getMotionMap();
    const observed = [];

    svgs.forEach(svg => {
        const firstPath = svg.querySelector('path');
        if (!firstPath) return;
        const entry = map[firstPath.getAttribute('d')];
        if (!entry) return;

        // Evitar doble movimiento: nuestro JS sustituye al "breath" CSS en estos tipos.
        svg.classList.remove('divider-animated');
        const paths = Array.from(svg.querySelectorAll('path'));

        if (entry.motion === 'parallax') {
            // Parallax = translateX sobre el path estático del catálogo (no se regenera).
            const layers = paths.map((path, i) => {
                const c = (entry.layers && entry.layers[i]) || {};
                // `range` = recorrido máx en user-units; acotado a < PAD para no mostrar bordes.
                const range = Math.min(c.speed ?? 60, PAD - 60);
                return { path, range };
            });
            const item = { svg, inView: false, kind: 'parallax', layers };
            svg._dmItem = item;
            parallaxItems.push(item);
            observed.push(svg);
        } else if (entry.motion === 'morph') {
            const layers = paths.map((path, i) => {
                const c = (entry.layers && entry.layers[i]) || {};
                return { path, baseline: c.baseline ?? 48, amp: c.amp ?? 16, freq: c.freq ?? 1.8, speedT: (c.speed ?? 0.45) / 1000, basePhase: i * 0.8 };
            });
            const item = { svg, inView: false, kind: 'morph', layers };
            svg._dmItem = item;
            morphItems.push(item);
            observed.push(svg);
        }
    });

    if (!observed.length) return;             // no creamos IO si no hay dividers motion

    io = new IntersectionObserver(onIntersect, { threshold: 0 });
    observed.forEach(svg => io.observe(svg));
    bindScroll();
}

// preview + público (nunca en edición).
registerPostRender(initDividerMotion, { previewOnly: true });
// preview→editor: limpiar listeners/observer/rAF (sin esta línea quedaban vivos en el editor).
registerPostRender(teardown, { editorOnly: true });
